import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import Utils from "util/Utils";
import { OpcUaCertificateDTO } from "../../serviceapi/dataCollectorApi";
import EnabledTag from "./EnabledTag";
import { formatCreatedAt, kindLabel, kindSeverity } from "./opcUaUtils";

interface CertificateDetailsDialogProps {
  certificate: OpcUaCertificateDTO;
  onDownload: (certificate: OpcUaCertificateDTO) => Promise<void>;
  close(): void;
}

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-row gap-3 align-items-start">
    <span className="font-medium" style={{ minWidth: "8rem", flexShrink: 0 }}>
      {label}
    </span>
    <span style={{ wordBreak: "break-all" }}>{children}</span>
  </div>
);

/**
 * Read-only view of everything the API exposes for a certificate. The backend does not
 * return validity dates, issuer or serial, so those are deliberately absent rather than
 * guessed at — see the OPC UA plan's open questions.
 */
const CertificateDetailsDialog = ({
  certificate,
  onDownload,
  close,
}: CertificateDetailsDialogProps) => {
  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Download .der"
        icon="pi pi-download"
        className="p-button-text"
        onClick={() => onDownload(certificate)}
        disabled={!certificate.thumbprint}
      />
      <Button label="Close" onClick={close} />
    </div>
  );

  return (
    <Dialog
      visible
      modal
      onHide={close}
      header="Certificate details"
      style={{ width: "min(600px, 95vw)" }}
      footer={footer}
    >
      <div className="flex flex-column gap-3">
        <DetailRow label="Subject">{certificate.subject || "—"}</DetailRow>
        <DetailRow label="Kind">
          <Tag
            value={kindLabel(certificate.kind)}
            severity={kindSeverity(certificate.kind)}
          />
        </DetailRow>
        <DetailRow label="Thumbprint">
          <span className="flex align-items-center gap-2 flex-wrap">
            <code>{certificate.thumbprint || "—"}</code>
            {certificate.thumbprint && (
              <Button
                className="p-button-text p-button-sm"
                icon="pi pi-copy"
                tooltip="Copy thumbprint"
                onClick={() =>
                  Utils.copyToClipboard(
                    certificate.thumbprint as string,
                    "Thumbprint"
                  )
                }
              />
            )}
          </span>
        </DetailRow>
        <DetailRow label="Username">{certificate.username || "—"}</DetailRow>
        <DetailRow label="Enabled">
          <EnabledTag enabled={certificate.enabled} />
        </DetailRow>
        <DetailRow label="Created">{formatCreatedAt(certificate.createdAt)}</DetailRow>
      </div>
    </Dialog>
  );
};

export default CertificateDetailsDialog;
